import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabaseClient';
import { fetchAppStatus } from '../../services/appStatusApi';
import './SiteModeGate.css';

// MODE TEST DU SITE (demande du 2026-10-17) — voir backend/routes/appStatus.js.
//
// Quand l'administration passe le site en mode test :
//   - les TESTEURS (administrateurs, liste des testeurs) voient le site, avec une pastille « Mode test » ;
//   - tous les AUTRES voient « Site en maintenance, nous reviendrons bientôt ».
// En production, ce composant n'affiche rien de plus que le site.
//
// Ce n'est pas une protection : le serveur reste la seule autorite sur les droits (produits reserves, commandes).
// C'est l'affichage, qui repond a la question « que doit voir ce visiteur ? ».
//
// Garde-fous :
//   - JAMAIS bloquant sur une panne : si l'etat est inconnu (erreur reseau, serveur injoignable), le site s'affiche
//     comme avant — une panne de cette route ne doit pas fermer le site a tout le monde ;
//   - dernier etat memorise (sessionStorage) pour ne pas attendre une reponse a chaque page ;
//   - les pages de connexion restent accessibles : un testeur doit pouvoir se connecter pour etre reconnu ;
//   - les pages legales aussi (mentions legales, CGV, confidentialite) : elles doivent rester consultables.

const CLE_ETAT = 'bookipix.siteStatus';
const INTERVALLE_MS = 60 * 1000;

// Chemins ouverts a tous meme en maintenance.
const CHEMINS_OUVERTS = [
  '/login', '/register', '/mot-de-passe-oublie', '/reinitialiser-mot-de-passe', '/mes-livres',
  '/mentions-legales', '/cgv', '/confidentialite', '/admin'
];
const estCheminOuvert = (chemin) => CHEMINS_OUVERTS.some((ouvert) => chemin === ouvert || chemin.startsWith(`${ouvert}/`));

const lireEtatMemorise = () => {
  try {
    const brut = sessionStorage.getItem(CLE_ETAT);
    return brut ? JSON.parse(brut) : null;
  } catch (_error) {
    return null;
  }
};
const memoriserEtat = (etat) => {
  try { sessionStorage.setItem(CLE_ETAT, JSON.stringify(etat)); } catch (_error) { /* sans stockage : sans memoire */ }
};

function MaintenancePage() {
  const { t } = useTranslation('common');
  return (
    <main className="site-maintenance" role="main">
      <div className="site-maintenance-card">
        <span className="site-header-logo site-maintenance-brand">
          <span className="site-header-mark" aria-hidden="true" />
          Bookipix
        </span>
        <h1>{t('siteMode.maintenance.title')}</h1>
        <p>{t('siteMode.maintenance.body')}</p>
        <Link className="site-maintenance-testers" to="/login">{t('siteMode.maintenance.testerLink')}</Link>
      </div>
    </main>
  );
}

function SiteModeGate({ children }) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [etat, setEtat] = useState(() => lireEtatMemorise());
  // Premiere reponse recue (ou echec) : sans etat memorise, on attend avant d'afficher quoi que ce soit.
  const [regle, setRegle] = useState(() => lireEtatMemorise() !== null);
  const actif = useRef(true);

  const charger = useCallback(async () => {
    try {
      const recu = await fetchAppStatus();
      if (!actif.current) return;
      const suivant = { maintenance: recu.maintenance === true, testMode: recu.testMode === true, tester: recu.tester === true };
      setEtat(suivant);
      memoriserEtat(suivant);
    } catch (_error) {
      // Etat inconnu : on garde le dernier connu (ou le site ouvert), jamais une fermeture sur une panne.
    } finally {
      if (actif.current) setRegle(true);
    }
  }, []);

  useEffect(() => {
    actif.current = true;
    charger();
    const minuteur = setInterval(charger, INTERVALLE_MS);
    // Une connexion / deconnexion change ce que voit le visiteur : on relit tout de suite.
    const { data: abonnement } = supabase.auth.onAuthStateChange(() => { charger(); });
    const surFocus = () => charger();
    window.addEventListener('focus', surFocus);
    return () => {
      actif.current = false;
      clearInterval(minuteur);
      window.removeEventListener('focus', surFocus);
      abonnement?.subscription?.unsubscribe();
    };
  }, [charger]);

  if (!regle) return <div className="site-gate-attente" aria-busy="true" />;

  if (etat?.maintenance && !estCheminOuvert(location.pathname)) return <MaintenancePage />;

  return (
    <>
      {children}
      {etat?.testMode && etat?.tester && (
        <div className="site-test-pastille" role="status" title={t('siteMode.banner.hint')}>
          <span className="site-test-pastille-point" aria-hidden="true" />
          {t('siteMode.banner.label')}
        </div>
      )}
    </>
  );
}

export default SiteModeGate;
